import * as React from 'react';

import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';

import Typography from '@mui/material/Typography';

import Course from './Course'

import { useTheme } from '@mui/material/styles';

import {data, filterOptions, categoryLabelToKey} from './Data'


export default function CoursePreviewTable({search, grouping, instructorFilter, levelFilter,
		cpFilter, attributeFilter, languageFilter, semesterFilter, showDetails, studyProgram}) {

	const theme = useTheme();
	
	// get list of semesters to show, according to available semesters and specification in the filters
	function filteredTableHeaders() {
		if (semesterFilter.length === 0) {
			return filterOptions[theme.language].semesters;
		} else {
			return semesterFilter;
		}
	}

	// compute data model based on raw data and configuration in the filters / grouping
	function tableGroups() {
		var tableHeaders = filteredTableHeaders();
		// groups is an array of arrays of objects with fields categoryGroup and option
		var groups = [[]];

		// create all possible combinations in the variable group
		grouping.forEach((groupCategory) => {
			var currentGroupCategoryOptions = filterOptions[theme.language][categoryLabelToKey[theme.language].get(groupCategory)];
			var newGroups = [];
			groups.forEach((prevGroup) => {
				currentGroupCategoryOptions.forEach((opt) => {
					var newGroup = [...prevGroup];
					newGroup.push({
						categoryKey: categoryLabelToKey[theme.language].get(groupCategory),
						option: opt
					});
					newGroups.push(newGroup);
				})
			});
			groups = newGroups;
		});

		// iterate over all possible combinations and determine the courses matching the options
		var result = [];
		for (const category of groups) {
			// group is an array of objects with
			// fields categoryKey and option

			var intermediateGroup = {};
			intermediateGroup.columns = [];

			for (let i = 0; i < tableHeaders.length; i++) {
				intermediateGroup.columns.push([]);
			}

			var maxGroupEntries = 0;
			// iterate over all semesters in the raw data table
			for (const semester of data[theme.language]) {
				const colIndex = tableHeaders.indexOf(semester.semesterName);
				// if the current semester is selected and should be displayed
				if (colIndex >= 0) {
					// iterate over all courses in this semester
					for (const semesterCourse of semester.semesterCourses) {
						// check group criteria
						// if the course matches all criteria of the current group
						var includeSemesterCourse = false;
						includeSemesterCourse ||= category.reduce((acc, group) => {
							var groupCriterionMatches = false;
							if (group.categoryKey === "levels")
								groupCriterionMatches = semesterCourse.level === group.option;
							else if (group.categoryKey === "creditPoints")
								groupCriterionMatches = semesterCourse.creditPoints === group.option;
							else if (group.categoryKey === "instructors")
								groupCriterionMatches = semesterCourse.instructors.indexOf(group.option) >= 0;
							else if (group.categoryKey === "attributes") {
								semesterCourse.studyAreas.forEach((area) => {
									if (area.attributes.indexOf(group.option) >= 0)
										groupCriterionMatches = true;
								})
							} else if (group.categoryKey === "languages")
								groupCriterionMatches = semesterCourse.languages.indexOf(group.option) >= 0;

							return acc && groupCriterionMatches
						}, true);

						// do not include the sabbatical
						if (semesterCourse.name === "_Forschungssemester") {
							includeSemesterCourse = false;
						}

						// check filters
						// only for defined filters
						if (instructorFilter.length > 0) {
							includeSemesterCourse &&= instructorFilter.reduce((acc, requiredInstructor) =>
								(acc || semesterCourse.instructors.indexOf(requiredInstructor) >= 0), false);
						}
						if (levelFilter.length > 0) {
							includeSemesterCourse &&= levelFilter.reduce((acc, requiredLevel) =>
								(acc || semesterCourse.level === requiredLevel), false);
						}
						if (cpFilter.length > 0) {
							includeSemesterCourse &&= cpFilter.reduce((acc, requiredCp) =>
								(acc || semesterCourse.creditPoints === requiredCp), false);
						}
						if (attributeFilter.length > 0) {
							includeSemesterCourse &&= attributeFilter.reduce((acc, requiredAttribute) =>
							(acc || semesterCourse.studyAreas.reduce((accInner, area) =>
								(accInner || area.attributes.indexOf(requiredAttribute) >= 0)
								, false)), false);
						}
						if (languageFilter.length > 0) {
							includeSemesterCourse &&= languageFilter.reduce((acc, requiredLanguage) =>
								(acc || semesterCourse.languages.indexOf(requiredLanguage) >= 0), false);
						}
						if (studyProgram !== "") {
							includeSemesterCourse &&= semesterCourse.studyAreas.reduce((acc, sa) =>
							(acc || sa.studyProgram === studyProgram), false)
						}

						// check quick search
						// only if a quick search phrase is provided
						// if the course's name matches the quick search
						// or one of the instructors's name matches the quick search
						if (search.length > 0) {
							includeSemesterCourse &&= semesterCourse.name.toUpperCase().indexOf(search.toUpperCase()) >= 0 ||
								semesterCourse.instructors.reduce((acc, instructor) => (
									acc || instructor.toUpperCase().indexOf(search.toUpperCase()) >= 0
								), false);
						}
						
						if (includeSemesterCourse) {
							// if all conditions hold, add the course to the
							// semester's column of the current group
							intermediateGroup.columns[colIndex].push(semesterCourse);
						}
					}
					// keep track of the maximum number of entries per semester in the current group 
					if (intermediateGroup.columns[colIndex].length > maxGroupEntries) {
						maxGroupEntries = intermediateGroup.columns[colIndex].length;
					}
				}
			}

			// if the group contains at least one entry in any shown semester
			if (maxGroupEntries > 0) {
				// create table date that contains the courses in this group
				var rows = [];
				for (let i = 0; i < maxGroupEntries; i++) {
					var row = [];
					for (let col = 0; col < tableHeaders.length; col++) {
						var matchingPerSemester = intermediateGroup.columns[col];
						if (matchingPerSemester.length >= i + 1) {
							row.push(matchingPerSemester[i]);
						} else {
							row.push(null);
						}
					}
					rows.push(row);
				}
				// add the group to the result
				result.push({category: category, rows: rows});
			}
		}
		return result;
	}

	// define GUI elements
	return (
		<React.Fragment>
			<TableContainer component={Paper}>
				<Table sx={{ minWidth: 650 }} aria-label="Course Preview">
					<TableHead>
						<TableRow>
							{filteredTableHeaders().map((sem) => (
								<TableCell><Typography variant="body2">{sem}</Typography></TableCell>
							))}
						</TableRow>
					</TableHead>
					{tableGroups().map((group) => (
						<React.Fragment>
							{group.category.length > 0 ?
								<TableRow
									key={group.category}
									sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
									style={{ backgroundColor: 'lightgrey' }}
								>
									<TableCell component="th" scope="row" align="center" colSpan={5}>
										<Typography variant="body2">
											{group.category.map((c) => c.option).join(", ")}
										</Typography>
									</TableCell>
								</TableRow>
								: <></>
							}
							{group.rows.map((row) => (
								<TableRow
									key={row.name}
									sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
								>
									{row.map((cell) => (
										<React.Fragment>
											<TableCell align="left" style={{ verticalAlign: 'top' }} >
												{cell === null ? <></> : <Course cell={cell} showDetails={showDetails} /> }
											</TableCell>
										</React.Fragment>
									))}
								</TableRow>
							))}
						</React.Fragment>
					))}
					<TableBody>
					</TableBody>
				</Table>
			</TableContainer>
		</React.Fragment>
	);
}